import React, { createContext, useContext, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { CustomModal, Input, UiButton, UiText } from '@shared/ui';

import cls from './MoveEquipmentInventoryQuantityProvider.module.css';
import { useMoveEquipmentInventoryQuantity } from '../model/useMoveEquipmentInventoryQuantity';

import type {
    MoveEquipmentInventoryQuantityContextValue,
    MoveEquipmentInventoryQuantityRequest,
} from '../model/types';

const MoveEquipmentInventoryQuantityContext =
    createContext<MoveEquipmentInventoryQuantityContextValue | null>(null);

interface MoveEquipmentInventoryQuantityProviderProps {
    children: React.ReactNode;
}

export const MoveEquipmentInventoryQuantityProvider = ({
    children,
}: MoveEquipmentInventoryQuantityProviderProps) => {
    const { t } = useTranslation();
    const {
        request,
        value,
        quantity,
        isValid,
        isPending,
        setValue,
        requestMove,
        cancelMove,
        submitMove,
    } = useMoveEquipmentInventoryQuantity();
    const contextValue = useMemo(() => ({ requestMove }), [requestMove]);

    return (
        <MoveEquipmentInventoryQuantityContext.Provider value={contextValue}>
            {children}
            <CustomModal
                isOpen={Boolean(request)}
                onHideModal={cancelMove}
                contentClasses={cls.content}
                needClose={!isPending}
            >
                <CustomModal.Header classes={cls.header}>
                    {t('MoveInventoryItemQuantityModal.modalTitle.text')}
                </CustomModal.Header>
                <CustomModal.Body classes={cls.body}>
                    <div className={cls.quantityRow}>
                        <Input
                            autoFocus
                            value={value}
                            inputClassName={cls.input}
                            disabled={isPending}
                            onFocus={(event) => event.target.select()}
                            onChange={(event) => {
                                if (/^[0-9,.\s]*$/.test(event.target.value)) {
                                    setValue(event.target.value);
                                }
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && isValid) {
                                    void submitMove();
                                }
                            }}
                        />
                        <UiText variant="subPlain" className={cls.fromText}>
                            {t('MoveInventoryItemQuantityModal.from.text')}
                        </UiText>
                        <span className={cls.amount}>
                            {request ? String(request.amount).replace('.', ',') : ''}
                        </span>
                    </div>
                    <UiText variant="subPlain" className={cls.error}>
                        {request && (!Number.isFinite(quantity) || quantity <= 0)
                            ? t('MoveInventoryItemQuantityModal.floatLess0Error.text')
                            : null}
                        {request && quantity > request.amount
                            ? t('MoveInventoryItemQuantityModal.amountError.text')
                            : null}
                    </UiText>
                </CustomModal.Body>
                <CustomModal.Footer classes={cls.footer}>
                    <UiButton
                        btnText={t('MoveInventoryItemQuantityModal.MoveBtn.text')}
                        disabled={!isValid || isPending}
                        onClick={() => {
                            void submitMove();
                        }}
                    />
                </CustomModal.Footer>
            </CustomModal>
        </MoveEquipmentInventoryQuantityContext.Provider>
    );
};

export const useMoveEquipmentInventoryQuantityContext = () => {
    const context = useContext(MoveEquipmentInventoryQuantityContext);

    if (!context) {
        throw new Error(
            'useMoveEquipmentInventoryQuantityContext must be used inside MoveEquipmentInventoryQuantityProvider'
        );
    }

    return context;
};

export type { MoveEquipmentInventoryQuantityRequest };
